import { z, type ZodTypeAny } from 'zod';
import { FormHelper } from '@presentation/components/FormHelper';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import { openFormModal } from '@presentation/shell/openFormModal';
import { renderRowActions } from '@presentation/shell/rowActions';
import { SecureTenantStore } from '@infra/persistence/SecureTenantStore';
import { uuid } from '@shared/utils/uuid';
import { ToastService } from '@presentation/components/Toast';
import { normalizeRegisterList, registerPayload } from '@application/persistence/moduleDataPayload';
import { DATE_ISO } from '@shared/validation/helpers';
import { onExclusive } from '@presentation/shell/viewDom';
import { assertLicenceAllowsWrite } from '@infra/tenancy/entityWriteGuard';
import { AuthError } from '@infra/auth/AuthService';
import { expiredLicenceCopy } from '@application/licence/LicenceExpiryCopy';

export interface LocalRecord {
  id: string;
}

export function upsertById<T extends LocalRecord>(list: T[], item: T): T[] {
  const idx = list.findIndex((x) => x.id === item.id);
  if (idx === -1) return [...list, item];
  const next = [...list];
  next[idx] = item;
  return next;
}

export function removeById<T extends LocalRecord>(list: T[], id: string): T[] {
  return list.filter((x) => x.id !== id);
}

export function ensureIds<T extends { id?: string }>(
  items: T[],
  prefix: string,
): Array<T & { id: string }> {
  return items.map((item) => ({
    ...item,
    id: item.id && item.id.length > 0 ? item.id : uuid(prefix),
  }));
}

export async function loadRegister<T>(
  tenantId: string,
  ns: string,
  fallback: T[] = [],
  legacyKey?: string,
): Promise<T[]> {
  const data = await SecureTenantStore.getJson<unknown>(
    tenantId,
    ns,
    registerPayload(fallback),
    legacyKey ?? `nexxus.${ns}.${tenantId}`,
  );
  return normalizeRegisterList<T>(data, fallback);
}

export async function saveRegister<T>(
  tenantId: string,
  ns: string,
  items: T[],
  legacyKey?: string,
): Promise<void> {
  try {
    assertLicenceAllowsWrite();
  } catch (err) {
    if (err instanceof AuthError && err.code === 'LICENCE_EXPIRED') {
      ToastService.error(expiredLicenceCopy().body);
    }
    throw err;
  }
  await SecureTenantStore.setJson(
    tenantId,
    ns,
    registerPayload(items),
    legacyKey ?? `nexxus.${ns}.${tenantId}`,
  );
}

export type FieldDef =
  | {
      kind: 'text' | 'number' | 'date' | 'email' | 'month';
      name: string;
      label: string;
      required?: boolean;
      min?: number;
      placeholder?: string;
      /** Regex source (sans /…/) pour valider la saisie texte. */
      pattern?: string;
      patternMessage?: string;
    }
  | {
      kind: 'select';
      name: string;
      label: string;
      required?: boolean;
      options: { value: string; label: string }[];
    };

function schemaFromFields(fields: FieldDef[]): z.ZodType<Record<string, unknown>> {
  const shape: Record<string, ZodTypeAny> = {};
  for (const f of fields) {
    if (f.kind === 'number') {
      let s = z.coerce.number();
      if (f.min !== undefined) s = s.min(f.min, `${f.label} invalide`);
      shape[f.name] = f.required === false ? s.optional() : s;
    } else if (f.kind === 'email') {
      const s = z.string().email(`${f.label} invalide`);
      shape[f.name] = f.required === false ? s.optional() : s.min(1, `${f.label} requis`);
    } else if (f.kind === 'date') {
      const s = z.string().regex(DATE_ISO, `${f.label} : date YYYY-MM-DD requise`);
      shape[f.name] = f.required === false ? s.optional() : s;
    } else {
      const hasPattern = 'pattern' in f && Boolean(f.pattern);
      const patternMsg =
        ('patternMessage' in f && f.patternMessage) || `${f.label} : format invalide`;
      if (f.required === false) {
        if (hasPattern && f.pattern) {
          const re = new RegExp(`^(?:${f.pattern})$`);
          shape[f.name] = z
            .string()
            .refine((v) => v === '' || re.test(v), patternMsg)
            .optional();
        } else {
          shape[f.name] = z.string().optional();
        }
      } else {
        let s = z.string().min(1, `${f.label} requis`);
        if (hasPattern && f.pattern) {
          s = s.regex(new RegExp(`^(?:${f.pattern})$`), patternMsg);
        }
        shape[f.name] = s;
      }
    }
  }
  return z.object(shape).superRefine((data, ctx) => {
    const names = new Set(fields.map((f) => f.name));
    const pairs: Array<[string, string]> = [
      ['date_debut', 'date_fin'],
      ['date', 'date_echeance'],
      ['date_facture', 'date_echeance'],
    ];
    for (const [start, end] of pairs) {
      if (!names.has(start) || !names.has(end)) continue;
      const a = data[start];
      const b = data[end];
      if (typeof a === 'string' && typeof b === 'string' && a && b && b < a) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [end],
          message: 'La date de fin précède la date de début',
        });
      }
    }
  });
}

export function openRegisterForm(opts: {
  parent: HTMLElement;
  title: string;
  size?: 'sm' | 'md' | 'lg';
  fields: FieldDef[];
  values?: object;
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
}): void {
  openFormModal(opts.parent, { title: opts.title, size: opts.size ?? 'md' }, (host, close) => {
    const fieldsHtml = opts.fields
      .map((f) => {
        const raw = opts.values as Record<string, unknown> | undefined;
        const val = raw?.[f.name];
        if (f.kind === 'select') {
          return FormHelper.select(f.name, f.label, f.options, {
            required: f.required !== false,
            value: val !== undefined && val !== null ? String(val) : undefined,
          });
        }
        const type = f.kind === 'month' ? 'month' : f.kind;
        return FormHelper.field(f.name, f.label, type, {
          required: f.required !== false,
          min: f.min,
          placeholder: f.placeholder,
          pattern: 'pattern' in f ? f.pattern : undefined,
          value: val as string | number | undefined,
        });
      })
      .join('');
    host.innerHTML = `
      <form id="local-crud-form">
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:8px;min-width:0;">
          ${fieldsHtml}
        </div>
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:16px;flex-wrap:wrap;">
          <button type="button" class="btn btn-ghost" data-cancel>Annuler</button>
          <button type="submit" class="btn btn-primary">Enregistrer</button>
        </div>
      </form>
    `;
    host.querySelector('[data-cancel]')?.addEventListener('click', close);
    const form = host.querySelector<HTMLFormElement>('#local-crud-form');
    if (!form) return;
    FormHelper.bindSubmit(form, schemaFromFields(opts.fields), async (data) => {
      await opts.onSubmit(data as Record<string, unknown>);
      ToastService.success('Enregistré');
      close();
    });
  });
}

export function bindCrudTable(
  tbody: HTMLElement | null,
  parent: HTMLElement,
  onEdit: (id: string) => void,
  onDelete: (id: string) => Promise<void>,
  canMutate = true,
): void {
  onExclusive(tbody, 'click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!btn?.dataset.id) return;
    const id = btn.dataset.id;
    if (!canMutate) {
      ToastService.warning('Action réservée aux profils autorisés');
      return;
    }
    if (btn.dataset.action === 'edit') onEdit(id);
    if (btn.dataset.action === 'delete') {
      void (async () => {
        const ok = await confirmAction({
          host: parent,
          title: 'Supprimer',
          message: 'Supprimer cette fiche ? Cette action est irréversible.',
          confirmLabel: 'Supprimer',
          danger: true,
        });
        if (!ok) return;
        await onDelete(id);
        ToastService.success('Fiche supprimée');
      })();
    }
  });
}

export function crudActions(id: string): string {
  return renderRowActions(id, [
    { action: 'edit', label: 'Modifier' },
    { action: 'delete', label: 'Supprimer', variant: 'danger' },
  ]);
}

export function emptyRow(colspan: number, message: string): string {
  return `<tr><td colspan="${colspan}" style="color:var(--gris);padding:14px;">${message}</td></tr>`;
}

export function tenantIdOf(ctx: {
  tenantContext: { tryGetTenant: () => { id: string } | null };
}): string {
  return ctx.tenantContext.tryGetTenant()?.id ?? 'default';
}
